/**
 * ============================================================================
 * AddSupplierDialog — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: modal to create a new supplier in PENDING_REQUEST. Per SUP-IMP-002,
 *          the server runs a fuzzy-name dedup pass and returns 409 +
 *          duplicate candidates when the new name looks similar enough to an
 *          existing supplier. We surface the candidates inline and let the
 *          user either pick one of them, dismiss with confirm_no_duplicate,
 *          or rename.
 *
 * A11Y: role="dialog" + aria-modal, initial focus on the first input, Escape
 *  closes. The duplicate-warning block has role="alert" so AT users hear it
 *  the moment it appears.
 * ============================================================================ */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './SupplierWorkbench.module.css';
import {
  useCreateSupplierMutation,
  type CreateSupplierInput,
  type CreateSupplierResponse,
} from './useCreateSupplierMutation';
import type {
  SupplierCriticality,
  SupplierSubtype,
} from './useSuppliersQuery';

const CRIT_OPTIONS: SupplierCriticality[] = ['CRITICAL', 'MAJOR', 'MINOR', 'LOW'];
const SUBTYPE_OPTIONS: SupplierSubtype[] = [
  'API',
  'EXCIPIENT',
  'PACKAGING',
  'EQUIPMENT',
  'CONTRACT_MFG',
  'CONTRACT_LAB',
  'CALIBRATION',
  'CONSUMABLE',
  'IT',
  'OTHER',
  'MATERIAL_SUPPLIER',
  'CMO',
  'CONSULTANT',
  'SERVICE_PROVIDER',
] as SupplierSubtype[];
// SUP-FULL — the qualification lifecycle states (matches the DB CHECK).
const LIFECYCLE_OPTIONS = [
  'PENDING_REQUEST', 'RISK_ASSESSED', 'QUESTIONNAIRE_ISSUED', 'QUESTIONNAIRE_RECEIVED',
  'AUDIT_SCHEDULED', 'AUDIT_IN_PROGRESS', 'CAPA_OPEN', 'CAPA_CLOSED', 'APPROVED',
  'ACTIVE', 'REQUAL_DUE', 'REQUALIFICATION', 'RESTRICTED', 'DISQUALIFIED',
];

export interface AddSupplierDialogProps {
  onClose: () => void;
  onCreated: (supplier: CreateSupplierResponse) => void;
}

export function AddSupplierDialog({ onClose, onCreated }: AddSupplierDialogProps) {
  const { t, i18n } = useTranslation();
  const pf = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 0,
  });
  const titleId = useId();
  const descId = useId();
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const createM = useCreateSupplierMutation();
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [criticality, setCriticality] = useState<SupplierCriticality>('MINOR');
  const [subtype, setSubtype] = useState<SupplierSubtype>('OTHER');
  const [countryCode, setCountryCode] = useState('');
  const [vatId, setVatId] = useState('');
  // SUP-FULL — full profile fields
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [singleSource, setSingleSource] = useState(false);
  const [riskCategory, setRiskCategory] = useState<SupplierCriticality | ''>('');
  const [riskScore, setRiskScore] = useState('');
  const [nextRequalAt, setNextRequalAt] = useState('');
  const [lifecycleStatus, setLifecycleStatus] = useState('PENDING_REQUEST');
  const [error, setError] = useState<string | null>(null);
  const [dupes, setDupes] = useState<CreateSupplierResponse['duplicate_candidates']>(
    undefined,
  );

  useEffect(() => {
    firstFieldRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(confirmNoDuplicate: boolean) {
    setError(null);
    if (!name.trim()) {
      setError(
        t('supplier.workbench.add.errors.nameRequired', {
          defaultValue: 'Supplier name is required.',
        }),
      );
      return;
    }
    const input: CreateSupplierInput = {
      name: name.trim(),
      legal_name: legalName.trim() || undefined,
      criticality,
      subtype,
      country_code: countryCode.trim() || undefined,
      vat_id: vatId.trim() || undefined,
      contact_email: contactEmail.trim() || undefined,
      contact_phone: contactPhone.trim() || undefined,
      single_source: singleSource || undefined,
      risk_category: riskCategory || undefined,
      risk_score: riskScore.trim() !== '' ? Number(riskScore) : undefined,
      next_requalification_at: nextRequalAt || undefined,
      lifecycle_status: lifecycleStatus || undefined,
      confirm_no_duplicate: confirmNoDuplicate || undefined,
    };
    try {
      const res = await createM.mutateAsync(input);
      if (res.duplicate_candidates && res.duplicate_candidates.length > 0) {
        // Server returned 200 with the suggestion list (some implementations
        // do this instead of 409). Surface the candidates rather than
        // pretending the record was created.
        setDupes(res.duplicate_candidates);
        return;
      }
      onCreated(res);
    } catch (e: unknown) {
      // 409 → server stuffs candidates in the detail. We don't parse here;
      // we just show the message verbatim and keep the form open.
      setError(
        e instanceof Error
          ? e.message
          : t('supplier.workbench.add.errors.serverFailed', {
              defaultValue: 'Server rejected the create.',
            }),
      );
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    void submit(false);
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('supplier.workbench.add.title', {
              defaultValue: 'Add supplier',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('supplier.workbench.add.subtitle', {
              defaultValue:
                'Create a new supplier in PENDING_REQUEST. The system will check for likely duplicates by name (SUP-IMP-002).',
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          {dupes && dupes.length > 0 && (
            <div role="alert" className={styles.dupesBanner}>
              <strong>
                {t('supplier.workbench.add.duplicates.title', {
                  defaultValue: 'Possible duplicates found',
                })}
              </strong>
              <p className={styles.dupesDesc}>
                {t('supplier.workbench.add.duplicates.body', {
                  defaultValue:
                    'These existing suppliers have similar names. Pick one to reuse, or confirm this is a new supplier.',
                })}
              </p>
              <ul className={styles.dupesList}>
                {dupes.map((d) => (
                  <li key={d.supplier_id} className={styles.dupesItem}>
                    <span>{d.name}</span>
                    <span className={styles.dupesScore}>
                      {pf.format(d.similarity)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className={styles.dupesActions}>
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={() => void submit(true)}
                  isLoading={createM.isPending}
                  aria-label={t('supplier.workbench.add.duplicates.confirmAria', {
                    defaultValue:
                      'Confirm this is a new supplier despite the duplicate suggestions',
                  })}
                >
                  {t('supplier.workbench.add.duplicates.confirm', {
                    defaultValue: 'Create anyway',
                  })}
                </Button>
              </div>
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="sup-add-name" className={dialogStyles.label}>
              {t('supplier.workbench.add.name', { defaultValue: 'Supplier name' })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="sup-add-name"
              ref={firstFieldRef}
              className={dialogStyles.input}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setDupes(undefined);
              }}
              required
            />
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="sup-add-legal" className={dialogStyles.label}>
              {t('supplier.workbench.add.legalName', {
                defaultValue: 'Legal name (optional)',
              })}
            </label>
            <input
              id="sup-add-legal"
              className={dialogStyles.input}
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
            />
          </div>

          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="sup-add-crit" className={dialogStyles.label}>
                {t('supplier.workbench.add.criticality', {
                  defaultValue: 'Criticality',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <select
                id="sup-add-crit"
                className={dialogStyles.select}
                value={criticality}
                onChange={(e) =>
                  setCriticality(e.target.value as SupplierCriticality)
                }
              >
                {CRIT_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {t(`supplier.criticality.${c}`, { defaultValue: c })}
                  </option>
                ))}
              </select>
            </div>

            <div className={dialogStyles.field}>
              <label htmlFor="sup-add-sub" className={dialogStyles.label}>
                {t('supplier.workbench.add.subtype', { defaultValue: 'Subtype' })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <select
                id="sup-add-sub"
                className={dialogStyles.select}
                value={subtype}
                onChange={(e) => setSubtype(e.target.value as SupplierSubtype)}
              >
                {SUBTYPE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`supplier.subtype.${s}`, { defaultValue: s })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="sup-add-cc" className={dialogStyles.label}>
                {t('supplier.workbench.add.country', {
                  defaultValue: 'Country code',
                })}
              </label>
              <input
                id="sup-add-cc"
                className={dialogStyles.input}
                value={countryCode}
                maxLength={2}
                onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
                placeholder="SA"
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="sup-add-vat" className={dialogStyles.label}>
                {t('supplier.workbench.add.vat', { defaultValue: 'VAT id' })}
              </label>
              <input
                id="sup-add-vat"
                className={dialogStyles.input}
                value={vatId}
                onChange={(e) => setVatId(e.target.value)}
              />
            </div>
          </div>

          {/* SUP-FULL — primary contact */}
          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="sup-add-email" className={dialogStyles.label}>
                {t('supplier.workbench.add.contactEmail', { defaultValue: 'Primary contact email' })}
              </label>
              <input id="sup-add-email" type="email" className={dialogStyles.input}
                value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                placeholder="qa@supplier.com" />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="sup-add-phone" className={dialogStyles.label}>
                {t('supplier.workbench.add.contactPhone', { defaultValue: 'Primary contact phone' })}
              </label>
              <input id="sup-add-phone" type="tel" className={dialogStyles.input}
                value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+966 5x xxx xxxx" />
            </div>
          </div>

          {/* SUP-FULL — qualification lifecycle + single-source */}
          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="sup-add-life" className={dialogStyles.label}>
                {t('supplier.workbench.add.lifecycle', { defaultValue: 'Qualification status' })}
              </label>
              <select id="sup-add-life" className={dialogStyles.select}
                value={lifecycleStatus} onChange={(e) => setLifecycleStatus(e.target.value)}>
                {LIFECYCLE_OPTIONS.map((l) => (
                  <option key={l} value={l}>{t(`supplier.lifecycle.${l}`, { defaultValue: l.replace(/_/g, ' ') })}</option>
                ))}
              </select>
            </div>
            <div className={dialogStyles.field}>
              <label className={dialogStyles.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 28 }}>
                <input type="checkbox" checked={singleSource} onChange={(e) => setSingleSource(e.target.checked)} />
                {t('supplier.workbench.add.singleSource', { defaultValue: 'Single-source supplier' })}
              </label>
            </div>
          </div>

          {/* SUP-FULL — risk + requalification */}
          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="sup-add-riskcat" className={dialogStyles.label}>
                {t('supplier.workbench.add.riskCategory', { defaultValue: 'Risk category' })}
              </label>
              <select id="sup-add-riskcat" className={dialogStyles.select}
                value={riskCategory} onChange={(e) => setRiskCategory(e.target.value as SupplierCriticality | '')}>
                <option value="">{t('supplier.workbench.add.riskNone', { defaultValue: '— Not assessed —' })}</option>
                {CRIT_OPTIONS.map((c) => (
                  <option key={c} value={c}>{t(`supplier.criticality.${c}`, { defaultValue: c })}</option>
                ))}
              </select>
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="sup-add-riskscore" className={dialogStyles.label}>
                {t('supplier.workbench.add.riskScore', { defaultValue: 'Risk score (0–100)' })}
              </label>
              <input id="sup-add-riskscore" type="number" min={0} max={100} step="0.1"
                className={dialogStyles.input} value={riskScore}
                onChange={(e) => setRiskScore(e.target.value)} placeholder="0" />
            </div>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="sup-add-requal" className={dialogStyles.label}>
              {t('supplier.workbench.add.nextRequal', { defaultValue: 'Next requalification due (optional)' })}
            </label>
            <input id="sup-add-requal" type="date" className={dialogStyles.input}
              value={nextRequalAt} onChange={(e) => setNextRequalAt(e.target.value)} />
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={createM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={createM.isPending}>
              {t('supplier.workbench.add.submit', {
                defaultValue: 'Create supplier',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
